import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Reveal, { Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { IMAGES, WHY_POINTS } from "../constants/content";
import { useSite } from "../context/SiteContext";

export default function About() {
  const { images } = useSite();
  return (
    <>
      <Seo title="About Razor Build | Licensed Wellington Builders, 10+ Years Experience" description="Razor Build is a Wellington residential building company with 10+ years of experience in renovations, alterations, extensions, bathrooms, kitchens and new residential construction." />
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Eyebrow light>About Razor Build</Eyebrow>
          <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl tracking-tight text-white display-expanded leading-[1.0]">
            Built on Experience.<br />Focused on Quality.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="text-lg md:text-xl text-ink/85 font-medium leading-relaxed">
              Razor Build is a Wellington residential building company with more than 10 years of experience.
            </p>
            <p className="mt-5 text-slate2 leading-relaxed">
              We undertake renovations, alterations, extensions, bathrooms, kitchens and new residential construction across the Wellington region.
            </p>
            <p className="mt-4 text-slate2 leading-relaxed">
              Our approach is straightforward: understand what the client wants to achieve, provide practical building solutions and complete the work properly.
            </p>
            <p className="mt-4 text-slate2 leading-relaxed">
              Good building isn't only about how the finished project looks. It also comes down to planning, workmanship, communication and getting the details right throughout construction.
            </p>
            <p className="mt-8 font-mono text-xs tracking-[0.25em] uppercase text-golddark">Build • Renovate • Maintain</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="w-full aspect-[4/5] bg-white border border-line flex items-center justify-center overflow-hidden">
              <img src={images.builder} alt="Razor Build builders working on a Wellington residential project" loading="lazy" className="w-full h-full object-contain" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Build", "Renovate", "Maintain"]} dark={false} />

      <section className="py-16 md:py-24 bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How We Work</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-ink display-expanded">What You Can Expect</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="bg-white border border-line p-7 h-full">
                  <span className="font-mono text-xs tracking-[0.25em] text-golddark">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display font-semibold text-lg text-ink">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-slate2 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/contact" data-testid="about-cta" className="mt-12 inline-flex items-center gap-2.5 bg-ink hover:bg-charcoal text-white font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-4 transition-colors">
              Start a Conversation <ArrowRight className="w-4 h-4 text-gold" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
